import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { List, Tooltip } from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Close,
  ShoppingCart,
  Notifications,
  LocalOffer,
  People,
  LocalShipping,
  LocalCarWash,
  Schedule,
  Dashboard as DashboardIcon,
  AccountBalance,
  Devices,
  LocalGasStation,
  Print,
  Pix,
  DirectionsCar,
  Block,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import { useUnit } from "effector-react";
import { SignOut } from "phosphor-react";
import {
  LayoutContainer,
  HeaderBar,
  HeaderLogo,
  UserNameText,
  StationText,
  UserStationContainer,
  LogoutButton,
  MainWithSidebar,
  Sidebar,
  SidebarToggle,
  SidebarList,
  SidebarListItem,
  MainContent,
  MobileDrawer,
  MobileDrawerHeader,
  MobileDrawerCloseButton,
  MobileDrawerList,
  MobileMenuButton,
} from "./NavWrapperStyles";
import { logout } from "../../utils/authMethods";
import { UserStore } from "../../stores/UserStore/UserStore";
import { privateRoutes } from "./Menus.consts";
import { ROLES } from "../../types/user.types";

const SIDEBAR_EXPANDED_KEY = "nav-sidebar-expanded";

const routeIcons: Record<string, React.ComponentType<{ sx?: object }>> = {
  sales: ShoppingCart,
  notifications: Notifications,
  coupons: LocalOffer,
  customers: People,
  fleets: LocalShipping,
  washingOperator: LocalCarWash,
  scheduleSettings: Schedule,
  dashboard: DashboardIcon,
  balanceRequests: AccountBalance,
  testDevice: Devices,
  gasOrders: LocalGasStation,
  printers: Print,
  pendingPayments: Pix,
  vehicles: DirectionsCar,
  blockedUsers: Block,
  feedbacks: FeedbackIcon,
};

function getRouteIcon(routeName: string) {
  const Icon = routeIcons[routeName];
  return Icon ? <Icon sx={{ fontSize: 24, flexShrink: 0 }} /> : null;
}

export default function NavWrapper() {
  const { username, station, role } = useUnit(UserStore);
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
      return stored !== "false";
    } catch {
      return true;
    }
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(sidebarExpanded));
    } catch {
      // ignore
    }
  }, [sidebarExpanded]);

  const handleLogout = () => {
    logout(navigate);
  };

  const filteredMenu = useMemo(() => {
    return Object.entries(privateRoutes)
      .filter(([, routeData]) => {
        const roles = routeData?.authorizedRoles;
        if (!roles || routeData?.show === false) return false;

        // Rotas para qualquer papel - exceto USER_APP
        if (roles.includes(ROLES.ANY)) {
          return role !== ROLES.USER_APP;
        }

        // Papéis padrão (ADMIN, VIEWER, OPERATOR, etc.)
        if (roles.includes(role as ROLES)) {
          return true;
        }

        return false;
      })
      .map(([routeName, routeData]) => ({
        routeName,
        ...routeData,
      }));
  }, [role]);

  const toggleSidebar = () => setSidebarExpanded((e) => !e);
  const openMobileDrawer = () => setMobileDrawerOpen(true);
  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const showSidebar = role !== ROLES.OPERATOR;

  const isSelected = (path: string) => {
    if (path.startsWith("/dashboard")) return location.pathname === "/dashboard";
    if (path.startsWith("/gas-orders")) return location.pathname.startsWith("/gas-orders");
    if (path.startsWith("/pending-payments")) return location.pathname.startsWith("/pending-payments");
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const renderSidebarItems = (expanded: boolean) =>
    filteredMenu.map((route) => {
      const path = route.path.split("?")[0];
      const icon = getRouteIcon(route.routeName);
      const item = (
        <SidebarListItem
          key={route.path}
          className={isSelected(path) ? "Mui-selected" : undefined}
          onClick={closeMobileDrawer}
        >
          <Link
            to={route.path}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flex: 1,
              color: "inherit",
              textDecoration: "none",
              minWidth: 0,
            }}
          >
            {icon}
            {expanded && (route.title ?? route.routeName)}
          </Link>
        </SidebarListItem>
      );
      if (!expanded) {
        return (
          <Tooltip key={route.path} title={route.title ?? route.routeName} placement="right">
            {item}
          </Tooltip>
        );
      }
      return item;
    });

  return (
    <LayoutContainer>
      <HeaderBar position="static">
        <div style={{ display: "flex", alignItems: "center" }}>
          {showSidebar && (
            <MobileMenuButton onClick={openMobileDrawer} aria-label="Abrir menu">
              <MenuIcon sx={{ fontSize: 28 }} />
            </MobileMenuButton>
          )}
          <HeaderLogo
            src={require("../../assets/images/logo-super-ducha.png")}
            alt="Super Ducha"
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserStationContainer>
            <UserNameText>{username}</UserNameText>
            <StationText>Posto {station}</StationText>
          </UserStationContainer>
          <LogoutButton onClick={handleLogout} aria-label="Sair">
            <SignOut size={24} />
          </LogoutButton>
        </div>
      </HeaderBar>

      <MainWithSidebar>
        {showSidebar && (
          <>
            <Sidebar $expanded={sidebarExpanded} data-expanded={sidebarExpanded}>
              <SidebarToggle onClick={toggleSidebar} aria-label={sidebarExpanded ? "Recolher menu" : "Expandir menu"}>
                {sidebarExpanded ? <ChevronLeft /> : <ChevronRight />}
              </SidebarToggle>
              <SidebarList>
                <List disablePadding>{renderSidebarItems(sidebarExpanded)}</List>
              </SidebarList>
            </Sidebar>

            <MobileDrawer
              anchor="left"
              open={mobileDrawerOpen}
              onClose={closeMobileDrawer}
            >
              <MobileDrawerHeader>
                <MobileDrawerCloseButton onClick={closeMobileDrawer} aria-label="Fechar menu">
                  <Close />
                </MobileDrawerCloseButton>
              </MobileDrawerHeader>
              <MobileDrawerList>
                <List disablePadding>{renderSidebarItems(true)}</List>
              </MobileDrawerList>
            </MobileDrawer>
          </>
        )}

        <MainContent>
          <Outlet />
        </MainContent>
      </MainWithSidebar>
    </LayoutContainer>
  );
}
